import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import cx from "classnames";
import type { LocationTooltipRow } from "../../../helpers/locationDisplayUtils";

const TOOLTIP_MIN_VISIBLE_WIDTH_PX = 175;
const TOOLTIP_GAP_PX = 4;

interface LocationHierarchyTooltipProps {
  rows: LocationTooltipRow[];
  anchorRect: DOMRect;
  containerRect: DOMRect;
  onClose: () => void;
}

const LocationHierarchyTooltip = ({
  rows,
  anchorRect,
  containerRect,
  onClose,
}: LocationHierarchyTooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const tooltipEl = tooltipRef.current;
      if (!tooltipEl) {
        return;
      }
      const under = document.elementFromPoint(event.clientX, event.clientY);
      if (
        under &&
        !tooltipEl.contains(under) &&
        !(under as HTMLElement).closest?.("[data-location-cell]")
      ) {
        onClose();
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, [onClose]);

  if (!rows.length) {
    return null;
  }

  const tooltipWidth = 280;
  let left = anchorRect.left;
  const containerRight = containerRect.right;
  if (left + tooltipWidth > containerRight) {
    left = Math.max(containerRect.left, containerRight - TOOLTIP_MIN_VISIBLE_WIDTH_PX);
  }
  const top = anchorRect.bottom + TOOLTIP_GAP_PX;

  const tooltip = (
    <div
      ref={tooltipRef}
      className="fixed z-[60] min-w-[12rem] max-w-[20rem] rounded-md border border-gray-200 bg-white px-3 py-2 shadow-md"
      style={{ left, top, width: tooltipWidth }}
      role="tooltip"
    >
      <div className="space-y-1">
        {rows.map((row) => (
          <div key={row.tierIndex} className="flex gap-2">
            <span className="w-[3.125rem] shrink-0 text-[11px] text-gray-500">
              {row.label}
            </span>
            <span
              className={cx(
                "text-xs text-gray-900",
                row.isDeepest && "font-medium",
              )}
            >
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return createPortal(tooltip, document.body);
};

export default LocationHierarchyTooltip;
